'use client';

import React, { useState, useEffect, useRef, useSyncExternalStore } from 'react';
import { Cookie, X } from 'lucide-react';
import Link from '@/components/LocalizedLink';
import { useVisiblePathname } from '@/components/useVisiblePathname';
import { getLocaleFromPathname } from '@/lib/locale-path';

const noopSubscribe = () => () => {};

const DICTIONARY = {
  'en-us': {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.',
    short: 'We use cookies to analyze traffic.',
    accept: 'Accept',
    decline: 'Decline',
    privacyPolicy: 'Privacy Policy',
  },
  'en-gb': {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.',
    short: 'We use cookies to analyse traffic.',
    accept: 'Accept',
    decline: 'Decline',
    privacyPolicy: 'Privacy Policy',
  },
  'en-ca': {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.',
    short: 'We use cookies to analyze traffic.',
    accept: 'Accept',
    decline: 'Decline',
    privacyPolicy: 'Privacy Policy',
  },
  'en-au': {
    title: 'We value your privacy',
    description: 'We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. By clicking "Accept", you consent to our use of cookies.',
    short: 'We use cookies to analyse traffic.',
    accept: 'Accept',
    decline: 'Decline',
    privacyPolicy: 'Privacy Policy',
  },
  'de-de': {
    title: 'Wir schätzen Ihre Privatsphäre',
    description: 'Wir verwenden Cookies, um Ihr Surferlebnis zu verbessern, personalisierte Inhalte bereitzustellen und unseren Datenverkehr zu analysieren. Durch Klicken auf „Akzeptieren“ stimmen Sie der Verwendung von Cookies zu.',
    short: 'Wir nutzen Cookies zur Analyse.',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',
    privacyPolicy: 'Datenschutzerklärung',
  },
  'fr-fr': {
    title: 'Respect de la vie privée',
    description: 'Nous utilisons des cookies pour améliorer votre expérience de navigation, proposer des contenus personnalisés et analyser notre trafic. En cliquant sur « Accepter », vous consentez à l\'utilisation de ces cookies.',
    short: 'Nous utilisons des cookies.',
    accept: 'Accepter',
    decline: 'Refuser',
    privacyPolicy: 'Politique de confidentialité',
  },
} as const;

export default function CookieConsent() {
  const mounted = useSyncExternalStore(noopSubscribe, () => true, () => false);
  const [show, setShow] = useState(false);
  const bannerRef = useRef<HTMLDivElement>(null);

  const pathname = useVisiblePathname();
  const locale = getLocaleFromPathname(pathname);
  
  // Fallback to 'en-us' if locale is not supported
  const t = DICTIONARY[locale as keyof typeof DICTIONARY] || DICTIONARY['en-us'];

  // Mount only after the page has loaded and the browser is idle, so the hero
  // (not this banner) is the LCP element.
  useEffect(() => {
    if (localStorage.getItem('woltrio_cookie_consent')) return;
    let idleId: number | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const reveal = () => {
      if ('requestIdleCallback' in window) {
        idleId = window.requestIdleCallback(() => setShow(true), { timeout: 3000 });
      } else {
        timer = setTimeout(() => setShow(true), 1500);
      }
    };
    if (document.readyState === 'complete') reveal();
    else window.addEventListener('load', reveal, { once: true });
    return () => {
      window.removeEventListener('load', reveal);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timer) clearTimeout(timer);
    };
  }, []);

  // Reserve space at the bottom of the page while the banner is open so it
  // never covers a CTA.
  useEffect(() => {
    const el = bannerRef.current;
    if (!show || !el) return;
    const body = document.body;
    const prev = body.style.paddingBottom;
    const sync = () => {
      body.style.paddingBottom = `${el.offsetHeight + 16}px`;
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => {
      ro.disconnect();
      body.style.paddingBottom = prev;
    };
  }, [show]);

  // Lets fixed elements (the WhatsApp button) move out from under the banner.
  useEffect(() => {
    if (!show) return;
    document.documentElement.dataset.cookieBanner = "open";
    return () => {
      delete document.documentElement.dataset.cookieBanner;
    };
  }, [show]);

  const handleAccept = () => {
    localStorage.setItem('woltrio_cookie_consent', 'accepted');
    setShow(false);
  };

  const handleDecline = () => {
    localStorage.setItem('woltrio_cookie_consent', 'declined');
    setShow(false);
  };

  // Prevent SSR/CSR hydration mismatch by rendering null on server
  if (!mounted) return null;

  return (
    <>
      {show && (
        <div
          ref={bannerRef}
          role="region"
          aria-label={t.title}
          className="cookie-consent-in fixed bottom-0 inset-x-0 md:bottom-4 md:left-auto md:right-4 md:max-w-md z-[100] px-3 py-2 md:p-5 md:rounded-2xl border-t md:border border-zinc-200/50 dark:border-zinc-800/50 bg-white/95 md:bg-white/80 dark:bg-zinc-950/95 md:dark:bg-zinc-950/80 backdrop-blur-xl shadow-2xl flex items-center md:flex-col md:items-stretch gap-2 md:gap-4"
        >
          {/* Mobile: one line, under 64px tall */}
          <p className="md:hidden flex-1 min-w-0 text-xs leading-snug text-zinc-600 dark:text-zinc-400 font-sans line-clamp-2">
            {t.short}{' '}
            <Link
              href="/privacy-policy"
              className="font-medium text-orange-600 dark:text-orange-500 underline"
            >
              {t.privacyPolicy}
            </Link>
          </p>

          {/* Desktop: full card */}
          <div className="hidden md:flex items-start gap-3">
            <div className="p-2 rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-500 shrink-0">
              <Cookie className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <p className="font-display font-semibold text-sm text-zinc-900 dark:text-zinc-50">
                {t.title}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-zinc-600 dark:text-zinc-400 font-sans">
                {t.description}{' '}
                <Link
                  href="/privacy-policy"
                  className="font-medium text-orange-600 dark:text-orange-500 hover:underline inline-block"
                >
                  {t.privacyPolicy}
                </Link>
              </p>
            </div>
            <button
              type="button"
              onClick={handleDecline}
              className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 transition-colors p-2.5 -m-2 cursor-pointer min-h-11 min-w-11 flex items-center justify-center"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex shrink-0 justify-end gap-2 md:gap-3 items-center">
            <button
              type="button"
              onClick={handleDecline}
              className="px-3 md:px-4 py-2.5 min-h-11 md:min-h-0 text-xs font-semibold text-zinc-700 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white bg-zinc-100 hover:bg-zinc-200/80 dark:bg-zinc-800/80 dark:hover:bg-zinc-700 active:scale-95 transition-all rounded-xl cursor-pointer border border-zinc-200/60 dark:border-zinc-700/50"
            >
              {t.decline}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="px-3 md:px-4 py-2.5 min-h-11 md:min-h-0 text-xs font-semibold text-background bg-foreground hover:bg-foreground/90 active:scale-95 transition-all rounded-xl shadow-lg shadow-orange-500/10 cursor-pointer"
            >
              {t.accept}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
